import { Button, Flex, Input, Text, Textarea } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { FormField } from '../../../components/FormField'
import { FormModal } from '../../../components/FormModal'
import { Icon } from '../../../components/Icon'
import { MaskedInput } from '../../../components/MaskedInput'
import { FormSelect, SearchableSelect } from '../../../components/Select'
import { capitalizeName, hasFullName, useAppToast } from '../../../helpers'
import { ExperimentalClassService } from '../../../services/ExperimentalClass/ExperimentalClassService'
import {
  EXPERIMENTAL_CLASS_TIMES,
  IExperimentalClassOptionRaw,
  IExperimentalClassStudentInput
} from '../../../services/ExperimentalClass/types'

interface IProps {
  isOpen: boolean
  onClose: () => void
  onCreated: () => void
}

const STUDENT_AGES = Array.from({ length: 10 }, (_, i) => i + 5) // 5..14

const emptyForm = {
  teacherId: '',
  turma: '',
  classDate: '',
  classTime: '',
  guardianName: '',
  guardianEmail: '',
  guardianPhone: '',
  courseId: '',
  observations: ''
}

export function ExperimentalClassFormModal({ isOpen, onClose, onCreated }: IProps) {
  const service = new ExperimentalClassService()
  const toast = useAppToast()
  const [saving, setSaving] = useState(false)
  const [consultants, setConsultants] = useState<IExperimentalClassOptionRaw[]>([])
  const [teachers, setTeachers] = useState<IExperimentalClassOptionRaw[]>([])
  const [courses, setCourses] = useState<IExperimentalClassOptionRaw[]>([])
  const [consultantId, setConsultantId] = useState('')
  const [consultantConfirmed, setConsultantConfirmed] = useState(false)
  const [form, setForm] = useState({ ...emptyForm })
  const [students, setStudents] = useState<IExperimentalClassStudentInput[]>([{ name: '', age: '' }])
  const [formErrors, setFormErrors] = useState<Record<string, string[]>>({})

  const error = (key: string) => formErrors[key]?.[0]

  const loadOptions = async () => {
    try {
      const [consultantsList, teachersList, coursesList] = await Promise.all([
        service.getConsultants(),
        service.getTeachers(),
        service.getCourses()
      ])
      setConsultants(consultantsList)
      setTeachers(teachersList)
      setCourses(coursesList)
    } catch {
      toast.error('Erro ao carregar as opções do formulário')
    }
  }

  // Reset the form every time the modal is opened.
  useEffect(() => {
    if (!isOpen) return
    setConsultantId('')
    setConsultantConfirmed(false)
    setForm({ ...emptyForm })
    setStudents([{ name: '', age: '' }])
    setFormErrors({})
    loadOptions()
  }, [isOpen])

  const selectedConsultantName = consultants.find((c) => String(c.id) === consultantId)?.name

  const updateStudent = (index: number, field: keyof IExperimentalClassStudentInput, value: string) => {
    const next = [...students]
    next[index] = { ...next[index], [field]: field === 'age' ? (value === '' ? '' : Number(value)) : value }
    setStudents(next)
  }

  const handleSave = async () => {
    setFormErrors({})

    const clientErrors: Record<string, string[]> = {}
    if (!hasFullName(form.guardianName)) {
      clientErrors.guardian_name = ['Informe o nome e sobrenome do responsável.']
    }
    students.forEach((student, index) => {
      if (!hasFullName(student.name)) {
        clientErrors[`students.${index}.name`] = ['Informe o nome e sobrenome do aluno.']
      }
    })
    if (Object.keys(clientErrors).length > 0) {
      setFormErrors(clientErrors)
      toast.warning('Verifique os campos do formulário')
      return
    }

    setSaving(true)

    service
      .create({
        commercial_consultant_id: Number(consultantId),
        teacher_id: Number(form.teacherId),
        course_id: Number(form.courseId),
        turma: form.turma,
        class_date: form.classDate,
        class_time: form.classTime,
        guardian_name: form.guardianName,
        guardian_email: form.guardianEmail,
        guardian_phone: form.guardianPhone,
        students: students.map((s) => ({ name: s.name, age: Number(s.age) })),
        observations: form.observations
      })
      .then(() => {
        toast.success('Aula experimental registrada com sucesso')
        setSaving(false)
        onCreated()
        onClose()
      })
      .catch((response) => {
        setSaving(false)
        // The API returns 400 for validation errors (see Exceptions\Handler); 422 kept as a fallback.
        const status = response.response?.status
        if ((status === 400 || status === 422) && response.response?.data?.errors) {
          setFormErrors(response.response.data.errors)
          toast.warning('Verifique os campos do formulário')
          return
        }
        toast.error('Erro ao registrar aula experimental')
      })
  }

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title="Confirmação de Agendamento de Aula Experimental"
      size="wide"
      footer={
        <>
          {!consultantConfirmed ? (
            <Button colorScheme="magenta" mr={3} onClick={() => setConsultantConfirmed(true)} disabled={!consultantId}>
              Continuar
            </Button>
          ) : (
            <Button colorScheme="magenta" mr={3} onClick={handleSave} type="button" disabled={saving}>
              {saving ? 'Salvando...' : 'Registrar'}
            </Button>
          )}
          <Button variant="ghost" onClick={onClose} type="button">
            Cancelar
          </Button>
        </>
      }
    >
      {!consultantConfirmed ? (
        <Flex direction="column" minH="100px" py={2}>
          <Text mb={4} color="gray.600">
            Quem é o Consultor Comercial responsável por este agendamento?
          </Text>
          <FormField label="Consultor Comercial">
            <SearchableSelect
              placeholder="Selecione o consultor..."
              value={consultantId}
              onChange={(value) => setConsultantId(value)}
              options={consultants.map((c) => ({ value: String(c.id), label: c.name }))}
            />
          </FormField>
        </Flex>
      ) : (
        <Flex direction="column">
          <Flex justify="space-between" align="center" bg="gray.50" borderRadius="md" p={3} mb={4}>
            <Text>
              Consultor Comercial: <strong>{selectedConsultantName}</strong>
            </Text>
            <Button size="xs" variant="outline" colorScheme="magenta" onClick={() => setConsultantConfirmed(false)}>
              Alterar Consultor Comercial
            </Button>
          </Flex>

          <FormField label="Professor" error={error('teacher_id')}>
            <SearchableSelect
              placeholder="Buscar professor..."
              value={form.teacherId}
              onChange={(value) => setForm({ ...form, teacherId: value })}
              options={teachers.map((t) => ({ value: String(t.id), label: t.name }))}
            />
          </FormField>

          <FormField label="Número da turma" mt={4} error={error('turma')}>
            <Input
              value={form.turma}
              inputMode="numeric"
              onChange={(e) => setForm({ ...form, turma: e.target.value.replace(/\D/g, '') })}
              disabled={saving}
            />
          </FormField>

          <Flex gap={4}>
            <Flex direction="column" flex={1}>
              <FormField label="Data da aula" mt={4} error={error('class_date')}>
                <Input
                  type="date"
                  value={form.classDate}
                  onChange={(e) => setForm({ ...form, classDate: e.target.value })}
                  disabled={saving}
                />
              </FormField>
            </Flex>
            <Flex direction="column" flex={1}>
              <FormField label="Horário da aula" mt={4} error={error('class_time')}>
                <FormSelect
                  isDisabled={saving}
                  value={form.classTime}
                  onChange={(value) => setForm({ ...form, classTime: value })}
                  options={EXPERIMENTAL_CLASS_TIMES.map((time) => ({ value: time, label: time.replace(':', 'h') }))}
                  placeholder="Selecione"
                />
              </FormField>
            </Flex>
          </Flex>

          <FormField label="Nome do responsável" mt={4} error={error('guardian_name')}>
            <Input
              value={form.guardianName}
              onChange={(e) => setForm({ ...form, guardianName: e.target.value })}
              onBlur={(e) => setForm((prev) => ({ ...prev, guardianName: capitalizeName(e.target.value) }))}
              disabled={saving}
            />
          </FormField>

          <Flex gap={4}>
            <Flex direction="column" flex={1}>
              <FormField label="E-mail do responsável" mt={4} error={error('guardian_email')}>
                <Input
                  type="email"
                  value={form.guardianEmail}
                  onChange={(e) => setForm({ ...form, guardianEmail: e.target.value })}
                  disabled={saving}
                />
              </FormField>
            </Flex>
            <Flex direction="column" flex={1}>
              <FormField label="Telefone do responsável" mt={4} error={error('guardian_phone')}>
                <MaskedInput
                  mask="phone"
                  value={form.guardianPhone}
                  onChange={(guardianPhone) => setForm({ ...form, guardianPhone })}
                  isDisabled={saving}
                />
              </FormField>
            </Flex>
          </Flex>

          <Text mt={6} mb={2} fontWeight="medium">
            Alunos
          </Text>
          {students.map((student, index) => (
            <Flex key={index} gap={4} align="flex-end" mb={2}>
              <FormField label={`Nome do aluno ${index + 1}`} error={error(`students.${index}.name`)} flex={2}>
                <Input
                  value={student.name}
                  onChange={(e) => updateStudent(index, 'name', e.target.value)}
                  onBlur={(e) => updateStudent(index, 'name', capitalizeName(e.target.value))}
                  disabled={saving}
                />
              </FormField>
              <Flex direction="column" flex={1}>
                <FormField label="Idade" error={error(`students.${index}.age`)}>
                  <FormSelect
                    isDisabled={saving}
                    value={student.age}
                    onChange={(value) => updateStudent(index, 'age', value)}
                    options={STUDENT_AGES.map((age) => ({ value: age, label: String(age) }))}
                    placeholder="Idade"
                  />
                </FormField>
              </Flex>
              {index === 1 && (
                <Button
                  colorScheme="red"
                  variant="outline"
                  onClick={() => setStudents([students[0]])}
                  disabled={saving}
                >
                  <Icon name="lni lni-trash-can" />
                </Button>
              )}
            </Flex>
          ))}
          {error('students') && <Text color="red.500">{error('students')}</Text>}
          {students.length < 2 && (
            <Button
              size="sm"
              variant="outline"
              colorScheme="magenta"
              mt={1}
              alignSelf="flex-start"
              onClick={() => setStudents([...students, { name: '', age: '' }])}
              disabled={saving}
            >
              + Adicionar mais 1 aluno
            </Button>
          )}

          <FormField label="Curso" mt={6} error={error('course_id')}>
            <SearchableSelect
              placeholder="Selecione o curso..."
              value={form.courseId}
              onChange={(value) => setForm({ ...form, courseId: value })}
              options={courses.map((c) => ({ value: String(c.id), label: c.name }))}
            />
          </FormField>

          <FormField label="Observações (opcional)" mt={4}>
            <Textarea
              value={form.observations}
              onChange={(e) => setForm({ ...form, observations: e.target.value })}
              disabled={saving}
            />
          </FormField>
        </Flex>
      )}
    </FormModal>
  )
}
